// Кои действия има резервацията — чиста логика без React (тестове:
// reservationActions.test.ts). Общо за картата в списъка и поповъра, за да не
// се разминават.

import type { OpenTable, Reservation } from "../../../api/reservations";
import { MINUTE_MS, formatClock, toDateKey } from "./dayTime";
import {
  isExpiredUpcoming,
  reservationBillIds,
  reservationEndMs,
  reservationStartMs,
  tableNameKey,
} from "./tableStatus";

// „Настани" най-рано N минути преди часа на резервацията (закъснелите — без
// ограничение). Чист интервал по време, БЕЗ граница на локалния ден: 00:10
// се настанява от 23:40 предната вечер. Огледало на BE `SEAT_EARLY_MINUTES`
// (app/features/reservations/models.py; seat → 409 „Резервацията може да се
// настани след HH:MM", за друг ден „след DD.MM HH:MM"); описана в
// docs/cross-repo-contract.md. Смениш ли я — и в BE.
export const SEAT_EARLY_MINUTES = 30;

/** От кога може да се натисне „Настани" (началото − SEAT_EARLY_MINUTES). */
export const seatFromMs = (reservation: Reservation) =>
  reservationStartMs(reservation) - SEAT_EARLY_MINUTES * MINUTE_MS;

/**
 * Предстоящата резервация на масата, която „идва" в прозореца на „Настани"
 * (началото ѝ е до SEAT_EARLY_MINUTES след „сега"): поповърът на свободна
 * маса или на заета от сметка без резервация я показва като „Резервация в
 * този час" с „Настани гостите" — същото, което вижда и картата. Само на
 * линията „сега" (`isAtNow`); в „Преглед към" масата е просто свободна/заета
 * до нея. null — няма такава или е още далеч.
 */
export const upcomingInSeatWindow = (
  next: Reservation | null,
  nowMs: number,
  isAtNow: boolean,
): Reservation | null => {
  if (!isAtNow || !next || next.status !== "upcoming") return null;
  const startMs = reservationStartMs(next);
  return startMs > nowMs && startMs <= nowMs + SEAT_EARLY_MINUTES * MINUTE_MS ? next : null;
};

/** Защо „Настани" не е достъпно (null — достъпно). */
export type SeatBlock =
  // Прозорецът на „Настани" се отваря чак в по-късен локален ден (утрешна
  // резервация): настаняването би отворило сметка СЕГА — бутонът не се показва.
  | "otherDay"
  // Масите ѝ са изтрити от схемата — BE връща 409 „няма маса".
  | "noTable"
  // Още е рано: прозорецът се отваря по-късно ДНЕС (в 23:00 за 00:10) — 409.
  | "tooEarly"
  // Маса на ДРУГА настанена резервация (по id или през сметката ѝ) — 409.
  | "occupied"
  // На масите има още отворена сметка на ПРЕДИШНА резервация („Гостите си
  // тръгнаха", но сметката не е закрита) — никога не се закача към нова
  // резервация; хостесата чака сервитьорът да я закрие — 409.
  | "previousBill";

/**
 * Ключът на обяснението под изключен „Настани" (поповър и title на картата);
 * за друг ден/без маса бутонът изобщо не се показва.
 */
export const SEAT_BLOCK_MESSAGE: Partial<Record<SeatBlock, string>> = {
  occupied: "reservations.popover.seatBlockedOccupied",
  tooEarly: "reservations.popover.seatBlockedEarly",
  previousBill: "reservations.popover.seatBlockedPreviousBill",
};

/** Изключеният „Настани" се показва (с обяснението); при другите блокове — изобщо не. */
export const isSeatShown = (seatBlock: SeatBlock | null) =>
  seatBlock === null || seatBlock === "occupied" || seatBlock === "tooEarly" || seatBlock === "previousBill";

/**
 * Заетостта на масите на резервацията В МОМЕНТА — огледало на проверката
 * на BE при „Настани".
 */
export interface SeatCheck {
  // Маси, които държи друга НАСТАНЕНА резервация: по id (и втората ѝ маса,
  // която няма своя сметка) или през сметката ѝ (billId), вече на наша маса.
  heldTables: string[];
  // Отворените сметки по масите на ПРЕДИШНА (ненастанена вече) резервация —
  // спират „Настани" (огледало на BE `_refuse_bills_of_other_reservations`).
  previousBills: OpenTable[];
  // Отворените сметки по масите, които не са на друга резервация (отворени
  // от сервитьор без резервация) — кандидатите, към които „Настани" може да
  // закачи резервацията.
  freeBills: OpenTable[];
}

export const NO_SEAT_CHECK: SeatCheck = { heldTables: [], previousBills: [], freeBills: [] };

export interface UpcomingActions {
  seatBlock: SeatBlock | null;
  // Заетите маси при "occupied" (иначе празно) — за обяснението.
  blockingTables: string[];
  // Сметката на предишната резервация при "previousBill" (иначе null) — за
  // „Сметката на предишната резервация на маса 1 (Иван) още не е закрита".
  previousBill: OpenTable | null;
  // От кога може да се настани (за „Може да се настани след 18:30").
  seatFromMs: number;
  // Отворените сметки, към които „Настани" може да закачи резервацията
  // (празно при изключен „Настани") — виж `seatPlanOf`.
  seatBills: OpenTable[];
  canCancel: boolean;
  canEdit: boolean;
}

/**
 * Масите и сметките по масите на резервацията — сравнението е по име и по id.
 * `reservations` — резервациите на деня ЗАЕДНО със сега настанените извън
 * него (`seatedNow` на изгледа „Утре"). Сметка на ДРУГА резервация
 * (`OpenTable.reservationId`) никога не е кандидат: на настанена — масата е
 * заета, иначе — сметка на предишна резервация.
 */
export const seatCheckOf = (
  reservation: Reservation,
  reservations: Reservation[],
  openTables: OpenTable[],
): SeatCheck => {
  const otherSeated = reservations.filter(
    (other) => other.id !== reservation.id && other.status === "seated",
  );
  const seatedIds = new Set(otherSeated.map((other) => other.id));
  const seatedBillIds = new Set(otherSeated.flatMap((other) => reservationBillIds(other)));
  const seatedTableIds = new Set(otherSeated.flatMap((other) => other.tableIds));
  const ownNameByKey = new Map(reservation.tableNames.map((name) => [tableNameKey(name), name]));

  const held = new Set(
    reservation.tableNames.filter((_, index) => seatedTableIds.has(reservation.tableIds[index])),
  );
  const previousBills: OpenTable[] = [];
  const freeBills: OpenTable[] = [];
  for (const bill of openTables) {
    const ownName = ownNameByKey.get(tableNameKey(bill.tableName));
    if (ownName === undefined) continue;
    const ownerId = bill.reservationId ?? null;
    const isOtherOwner = ownerId !== null && ownerId !== reservation.id;
    if (seatedBillIds.has(bill.billId) || (isOtherOwner && seatedIds.has(ownerId))) held.add(ownName);
    else if (isOtherOwner) previousBills.push(bill);
    else freeBills.push(bill);
  }
  return {
    heldTables: reservation.tableNames.filter((name) => held.has(name)),
    previousBills,
    freeBills,
  };
};

/**
 * Действията на предстояща резервация спрямо истинското „сега". `check` —
 * резултатът от `seatCheckOf` (има смисъл само в прозореца на „Настани":
 * отворените сметки са сегашните; за друг ден — `NO_SEAT_CHECK`).
 */
export const upcomingActionsOf = (
  reservation: Reservation,
  nowMs: number,
  check: SeatCheck,
): UpcomingActions | null => {
  if (reservation.status !== "upcoming") return null;
  // Изтекъл интервал: синхронизацията (BE при seat → 409 „Само предстояща…",
  // тук `expireFinished`) я прави „Неизпълнена" — без действия, като `expired`.
  if (isExpiredUpcoming(reservation, nowMs)) return null;
  // „Настани" е интервал по време, не „днешният ден" (огледало на BE
  // `_require_seatable_now`): преди start − SEAT_EARLY_MINUTES е „рано", а
  // отваря ли се прозорецът чак в по-късен локален ден — „друг ден" (скрит).
  // Вчерашна, чийто час още тече след полунощ, минава — началото ѝ е минало.
  const seatFrom = seatFromMs(reservation);
  const beforeWindow = nowMs < seatFrom;
  const windowOpensToday = toDateKey(new Date(seatFrom)) === toDateKey(new Date(nowMs));
  let seatBlock: SeatBlock | null = null;
  let blockingTables: string[] = [];
  let previousBill: OpenTable | null = null;
  if (beforeWindow && !windowOpensToday) seatBlock = "otherDay";
  else if (reservation.tableNames.length === 0) seatBlock = "noTable";
  else if (beforeWindow) seatBlock = "tooEarly";
  else if (check.heldTables.length > 0) {
    seatBlock = "occupied";
    blockingTables = check.heldTables;
  } else if (check.previousBills.length > 0) {
    seatBlock = "previousBill";
    previousBill = check.previousBills[0];
  }
  return {
    seatBlock,
    blockingTables,
    previousBill,
    seatFromMs: seatFrom,
    seatBills: seatBlock === null ? check.freeBills : [],
    canCancel: true,
    canEdit: true,
  };
};

/**
 * Какво прави „Настани" по отворените сметки на масите: без сметка — BE
 * отваря нова празна; с една — закача се към нея; с няколко — питаме „При
 * кой сервитьор да настаним?" (най-рано отворената е първа).
 */
export type SeatPlan =
  | { kind: "newBill" }
  | { kind: "attach"; bill: OpenTable }
  | { kind: "pick"; bills: OpenTable[] };

export const seatPlanOf = (bills: OpenTable[]): SeatPlan => {
  if (bills.length === 0) return { kind: "newBill" };
  if (bills.length === 1) return { kind: "attach", bill: bills[0] };
  return {
    kind: "pick",
    bills: [...bills].sort((left, right) => Date.parse(left.openedAt) - Date.parse(right.openedAt)),
  };
};

/**
 * Кандидатите на модала „При кой сервитьор да настаним?" — само при 2+
 * (иначе [] и модалът се затваря). Смята се наново при всеки рендер от
 * текущите отворени сметки, не от снимка при отварянето на модала.
 */
export const seatPickBillsOf = (bills: OpenTable[]): OpenTable[] => {
  const plan = seatPlanOf(bills);
  return plan.kind === "pick" ? plan.bills : [];
};

/**
 * „Отмени настаняването" — само настанена, чийто час още тече. След края на
 * часа BE отказва (409; огледало на `is_overdue`): върната в „Предстоящи",
 * синхронизацията веднага би я направила „Неизпълнена".
 */
export const canUnseat = (reservation: Reservation, nowMs: number) =>
  reservation.status === "seated" && reservationEndMs(reservation) > nowMs;

/**
 * Празните сметки, които „Отмени настаняването" ще затвори: BE ги премахва,
 * щом са отворени от самото „Настани" и никой още не е поръчал
 * (`closesOnUnseat` в `openTables`) — при „Отделна сметка за всяка маса" за
 * всяка поотделно, по реда на сметките. [] — сметките остават в Маркиране
 * (има поръчка, закачени са към сметка на сервитьор или вече ги няма).
 */
export const unseatClosingBillsOf = (reservation: Reservation, openTables: OpenTable[]): OpenTable[] =>
  reservationBillIds(reservation).flatMap((billId) => {
    const bill = openTables.find((candidate) => candidate.billId === billId && candidate.closesOnUnseat === true);
    return bill ? [bill] : [];
  });

/** „Върни" — само отказана (обратно в „Предстоящи"). */
export const canRestore = (reservation: Reservation) => reservation.status === "cancelled";

/** Текстът под/върху „Настани": ключ на превода + параметрите му. */
export interface SeatHint {
  key: string;
  params: Record<string, string | number>;
}

/**
 * Обяснението на „Настани" (поповър и title на картата): защо е изключен,
 * към чия сметка ще се закачи, или че ще питаме при кой сервитьор. null —
 * отваря нова сметка (без подсказка) или бутонът изобщо не се показва.
 */
export const seatHintOf = (available: UpcomingActions): SeatHint | null => {
  const blockKey = available.seatBlock ? SEAT_BLOCK_MESSAGE[available.seatBlock] : undefined;
  if (blockKey && available.previousBill) {
    const bill = available.previousBill;
    return {
      key: bill.waiterName ? blockKey : `${blockKey}NoWaiter`,
      params: { table: bill.tableName, waiter: bill.waiterName ?? "" },
    };
  }
  if (blockKey) {
    return {
      key: blockKey,
      params: {
        tables: available.blockingTables.join(", "),
        count: available.blockingTables.length,
        time: formatClock(available.seatFromMs),
      },
    };
  }
  if (available.seatBlock !== null) return null;
  const plan = seatPlanOf(available.seatBills);
  if (plan.kind === "pick") return { key: "reservations.popover.seatPickHint", params: {} };
  if (plan.kind === "attach") {
    return plan.bill.waiterName
      ? { key: "reservations.popover.seatAttachHint", params: { waiter: plan.bill.waiterName } }
      : { key: "reservations.popover.seatAttachHintNoWaiter", params: {} };
  }
  return null;
};

/**
 * Какво да направи „Настани" по ПРЕСНИТЕ данни (единственият път към seat —
 * картата, поповърът на масата и „Резервация в този час" минават оттук):
 * спрян (друг терминал я е настанил, масата е заета, още е рано…), нова
 * сметка, закачане към единствената или изборът „При кой сервитьор".
 */
export type SeatDecision = { kind: "blocked"; hint: SeatHint | null } | SeatPlan;

export const seatDecisionOf = (available: UpcomingActions | null): SeatDecision => {
  if (!available || available.seatBlock !== null) {
    return { kind: "blocked", hint: available ? seatHintOf(available) : null };
  }
  return seatPlanOf(available.seatBills);
};
